import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { useLanguage } from '../contexts/LanguageContext';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Search } from 'lucide-react';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const PartywiseVoting = () => {
  const { t, language } = useLanguage();
  const [data, setData] = useState([]);
  const [filteredData, setFilteredData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [zoneFilter, setZoneFilter] = useState('all');
  const [searchTerm, setSearchTerm] = useState('');

  useEffect(() => {
    fetchData();
  }, []);

  useEffect(() => {
    applyFilters();
  }, [data, zoneFilter, searchTerm]);

  const fetchData = async () => {
    try {
      const response = await axios.get(`${API}/partywise-voting`);
      setData(response.data);
    } catch (error) {
      console.error('Error fetching data:', error);
      toast.error(t('error'));
    } finally {
      setLoading(false);
    }
  };

  const applyFilters = () => {
    let filtered = [...data];

    if (zoneFilter !== 'all') {
      filtered = filtered.filter(item => item.zone === zoneFilter);
    }

    if (searchTerm) {
      filtered = filtered.filter(item => 
        item.zone?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        item.prabhag_no?.toLowerCase().includes(searchTerm.toLowerCase())
      );
    }

    setFilteredData(filtered);
  };

  const zones = ['all', ...new Set(data.map(item => item.zone).filter(Boolean))];

  if (loading) {
    return <div className={`text-center py-12 ${language === 'mr' ? 'marathi-text' : ''}`}>{t('loading')}</div>;
  }

  return (
    <div className="space-y-6" data-testid="partywise-voting-page">
      <div>
        <h1 className={`text-3xl font-bold text-primary mb-2 ${language === 'mr' ? 'marathi-text' : ''}`}>
          {t('partywiseVoting')}
        </h1>
        <p className={`text-muted-foreground ${language === 'mr' ? 'marathi-text' : ''}`}>
          {language === 'en' ? 'Statement 8: Vote distribution by party' : 'विधान 8: पक्षानुसार मत वितरण'}
        </p>
      </div>

      {/* Filters */}
      <Card>
        <CardContent className="pt-6">
          <div className="flex flex-col md:flex-row gap-4">
            <div className="flex-1">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                <Input
                  placeholder={language === 'en' ? 'Search by zone or prabhag...' : 'झोन किंवा प्रभागानुसार शोधा...'}
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="pl-10"
                  data-testid="search-input"
                />
              </div>
            </div>
            <Select value={zoneFilter} onValueChange={setZoneFilter}>
              <SelectTrigger className="w-full md:w-48" data-testid="zone-filter">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {zones.map(zone => (
                  <SelectItem key={zone} value={zone}>
                    {zone === 'all' ? (language === 'en' ? 'All Zones' : 'सर्व झोन') : zone}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </CardContent>
      </Card>

      {/* Data Table */}
      <Card>
        <CardHeader>
          <CardTitle className={language === 'mr' ? 'marathi-text' : ''}>
            {filteredData.length} {language === 'en' ? 'Records' : 'रेकॉर्ड'}
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead className={language === 'mr' ? 'marathi-text' : ''}>{t('zone')}</TableHead>
                  <TableHead className={language === 'mr' ? 'marathi-text' : ''}>{language === 'en' ? 'Prabhag' : 'प्रभाग'}</TableHead>
                  <TableHead className={language === 'mr' ? 'marathi-text' : ''}>{t('reservation')}</TableHead>
                  <TableHead>BJP</TableHead>
                  <TableHead>INC</TableHead>
                  <TableHead>NCP</TableHead>
                  <TableHead>SS</TableHead>
                  <TableHead>BSP</TableHead>
                  <TableHead>MNS</TableHead>
                  <TableHead>AAP</TableHead>
                  <TableHead className={language === 'mr' ? 'marathi-text' : ''}>{language === 'en' ? 'Independent' : 'अपक्ष'}</TableHead>
                  <TableHead>NOTA</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {filteredData.length > 0 ? (
                  filteredData.map((item) => (
                    <TableRow key={item.id}>
                      <TableCell className="font-medium">{item.zone}</TableCell>
                      <TableCell>{item.prabhag_no}</TableCell>
                      <TableCell>{item.reservation}</TableCell>
                      <TableCell className="font-semibold text-primary">{item.bjp?.toLocaleString()}</TableCell>
                      <TableCell>{item.inc?.toLocaleString()}</TableCell>
                      <TableCell>{item.ncp?.toLocaleString()}</TableCell>
                      <TableCell>{item.ss?.toLocaleString()}</TableCell>
                      <TableCell>{item.bsp?.toLocaleString()}</TableCell>
                      <TableCell>{item.mns?.toLocaleString()}</TableCell>
                      <TableCell>{item.aap?.toLocaleString()}</TableCell>
                      <TableCell>{item.independent?.toLocaleString()}</TableCell>
                      <TableCell className="text-muted-foreground">{item.nota?.toLocaleString()}</TableCell>
                    </TableRow>
                  ))
                ) : (
                  <TableRow>
                    <TableCell colSpan={12} className="text-center py-8">
                      <div className={language === 'mr' ? 'marathi-text' : ''}>{t('noData')}</div>
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default PartywiseVoting;